import {
  Container,
  Typography,
} from "@mui/material";
import {
  DateValidationError,
} from "@mui/x-date-pickers";
import { isValid } from "date-fns";
import ExpandableTable, {
  DEFAULT_ROWS_PER_PAGE,
  ExecuteMultipleAction,
  TableCellType,
  TableSortType,
} from "components/ExpandableTable";
import ConfirmDialog from "components/ConfirmDialog";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { ActionsCell } from "pages/ExportedScormFiles/ActionsCell";
import { ExportedScormFilesFilters } from "pages/ExportedScormFiles/ExportedScormFilesFilters";
import { StatusCell } from "pages/ExportedScormFiles/StatusCell";
import { ExportedScormFileRow } from "pages/ExportedScormFiles/types";
import {
  hasAnswered,
  updatedOnOf,
  uploadOutcome,
  QueuedAttempt,
} from "pages/ExportedScormFiles/queuedAttempt";
import { useQuery, useQueryClient } from "react-query";
import {
  FileResponse,
  ListRequestOfScormExportedFilesFilter,
  ListResponseOfScormExportedFile,
  ScormExportedFile,
  ScormExportedFilesDownloadRequest,
  ScormExportedFilesFilter,
  ScormExportedFilesUploadRequest,
  ScormExportFileDeliveryStatus,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore, { PersistanceStore } from "stores/persistanceStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { getUtcDate } from "utils/dateUtils";

const LIST_QUERY_KEY = "scormExportedFilesService.getExportedFiles";

const TAGS_QUERY_KEY = "scormExportedFilesService.getExportedFileTags";

const POLL_INTERVAL_MS = 5000;

// How long a row stays un-clickable after this screen queues it. One refresh cycle: long enough that a second
// click cannot be the same impulse as the first, short enough that a message which never arrives does not
// strand the row. It is deliberately NOT "until the attempt reports" - the contract carries no attempt
// timestamp, so the screen cannot tell a slow attempt from a lost one, and blocking on the optimistic reading
// is what left a row disabled for the life of the page.
const UPLOAD_BLOCK_MS = POLL_INTERVAL_MS;

// How long the screen keeps polling for a row it queued. Past this it stops asking: an attempt that has not
// reported in two minutes is not going to be explained by another refetch, and a poll with no end is a request
// every five seconds for as long as the tab is open.
const UPLOAD_WATCH_LIMIT_MS = 120_000;

const FALLBACK_FILE_NAME = "exported-scorm-file.zip";

const FALLBACK_ARCHIVE_NAME = "exported-scorm-files.zip";

// A rejected date leaves the typed text in the field and the outline red. Without these the operator sees a
// date the grid is not answering and nothing saying why.
// The row is a plain object rather than the generated class: spreading an instance keeps its data and
// drops its methods, which is all a table row needs.
// Second line of defence, and the null-to-undefined conversion the store wants. The callers already reject
// on validationError, which covers a half typed date too - but see filterKey for what storing one costs, and
// a crash-class hazard is worth guarding twice.
const normalizeFilterDate = (date: Date | null) =>
  date && isValid(date) ? date : undefined;

// The one place both filters agree on how they look, so a change lands once. Outlined at the small size is
// what the Tag and Status filters beside these use: the standard variant reserves space above the input for
// its label, which leaves the field sitting lower than they do however the row is aligned.
const saveFile = (file: FileResponse, fallbackName: string) => {
  const url = URL.createObjectURL(file.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.fileName ?? fallbackName;
  link.click();
  URL.revokeObjectURL(url);
};

const ExportedScormFiles = () => {
  const { formatMessage: t } = useIntl();
  const { scormExportedFilesService } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const queryClient = useQueryClient();

  // Every column is unsortable, so these only exist to satisfy the table's required props: leaving
  // SortField unset is what makes the service apply its own order, newest export first then file name.
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [snackbarMessage, setSnackbarMessage] =
    useState<SnackbarMessageProps | null>(null);

  // When this screen asked delivery to push each row, so a second click cannot follow the first while the
  // attempt is still in flight. Delivery is the authority on what is actually sent - it re-reads every row
  // and skips anything already delivered - but a row stays Pending for as long as a transient failure is
  // retried, so the screen has to remember what it asked for.
  //
  // An entry leaves when the row reports Uploaded or Failed - the attempt answering - or when the breadcrumb
  // organization changes. It is deliberately NOT dropped when the screen stops polling for it: the tooltip
  // warning an operator that a previous attempt never reported reads this map, and forgetting the attempt
  // would withdraw that warning at the moment the evidence is strongest.
  //
  // The timestamp is what carries the two age thresholds. An id alone could only ever be present or absent,
  // so a lost message disabled its row for the life of the page and polled for it forever.
  //
  // ⛔ Two values, on two different clocks, and they must not be mixed. queuedAt is this browser's clock and
  // only ever feeds a DURATION - the age thresholds below - which is a comparison of that clock against
  // itself. baselineUpdatedOn is the server's, and is only ever compared against another server value.
  // Comparing the two directly is what the first attempt at this got wrong: a browser running minutes ahead
  // would leave every attempt looking unanswered forever, and one running behind would call every attempt
  // answered the instant it was queued.
  const [queuedAtByRunFileId, setQueuedAtByRunFileId] = useState<
    Record<string, QueuedAttempt>
  >({});

  // The row awaiting its own confirmation. Held rather than acted on directly, because the row icon is one
  // click away from a push to a customer's live destination.
  const [fileToUpload, setFileToUpload] =
    useState<ExportedScormFileRow | null>(null);

  const tag = persistanceStore.useState((s) => s.exportedScormFilesTag);
  const status = persistanceStore.useState((s) => s.exportedScormFilesStatus);
  const startDate = persistanceStore.useState(
    (s) => s.exportedScormFilesStartDate
  );
  const endDate = persistanceStore.useState((s) => s.exportedScormFilesEndDate);

  const ageOfQueuedAttempt = (id: string) => {
    const attempt = queuedAtByRunFileId[id];

    return attempt === undefined ? undefined : Date.now() - attempt.queuedAt;
  };

  const isWithinUploadBlock = (id: string) => {
    const age = ageOfQueuedAttempt(id);

    return age !== undefined && age < UPLOAD_BLOCK_MS;
  };

  const isWithinUploadWatch = (id: string) => {
    const age = ageOfQueuedAttempt(id);

    return age !== undefined && age < UPLOAD_WATCH_LIMIT_MS;
  };

  const [startDateError, setStartDateError] = useState<DateValidationError>(null);
  const [endDateError, setEndDateError] = useState<DateValidationError>(null);

  // minDate and maxDate validate, they do not clamp: a typed out of range date still reaches onChange and
  // only sets validationError. It is rejected here and explained by the helper text the slot props carry.
  const applyFilterDate = (
    date: Date | null,
    context: { validationError: DateValidationError },
    apply: (store: PersistanceStore, value: Date | undefined) => void
  ) => {
    if (context.validationError) {
      return;
    }

    persistanceStore.update((s) => apply(s, normalizeFilterDate(date)));
  };

  const applyStartDate = (
    date: Date | null,
    context: { validationError: DateValidationError }
  ) =>
    applyFilterDate(date, context, (s, value) => {
      s.exportedScormFilesStartDate = value;
    });

  const applyEndDate = (
    date: Date | null,
    context: { validationError: DateValidationError }
  ) =>
    applyFilterDate(date, context, (s, value) => {
      s.exportedScormFilesEndDate = value;
    });

  useListTitle("exportedScormFiles");

  // Reset during render rather than in an effect: an effect would first fire a request for the stale page
  // and flash an empty grid before correcting itself.
  //
  // ⚠️ This is where an unusable date costs the screen rather than the query: toISOString throws RangeError
  // on an Invalid Date, and it is called here during render. Both the onChange guards and
  // normalizeFilterDate exist to keep one out of the store, because the filter outlives navigation inside
  // the app - so a stored Invalid Date would crash the screen again on every visit.
  const filterKey = [
    organizationId ?? "",
    tag ?? "",
    status ?? "",
    startDate?.toISOString() ?? "",
    endDate?.toISOString() ?? "",
  ].join("|");
  const [appliedFilterKey, setAppliedFilterKey] = useState(filterKey);

  if (filterKey !== appliedFilterKey) {
    setAppliedFilterKey(filterKey);
    setPage(0);
  }

  // The queued rows belong to the organization they were queued under: the message carries the scope it was
  // published in, so ids from a previous breadcrumb organization describe rows this scope cannot even see.
  // Kept apart from the filter reset above, which fires on a tag or date change too - those do not
  // invalidate an attempt that is genuinely in flight.
  const [scopeQueuedUnder, setScopeQueuedUnder] = useState(organizationId);

  if (scopeQueuedUnder !== organizationId) {
    setScopeQueuedUnder(organizationId);
    setQueuedAtByRunFileId({});

    // The pending confirmation goes too, and it matters more than the record of past ones: it names a file
    // from the old scope, and confirming it would queue a row this scope will never see settle.
    setFileToUpload(null);
  }

  const tagsQuery = useQuery(
    [TAGS_QUERY_KEY, organizationId],
    () => scormExportedFilesService.getExportedFileTags(),
    { enabled: !!organizationId }
  );

  const { data, isFetching } = useQuery<ListResponseOfScormExportedFile>(
    [
      LIST_QUERY_KEY,
      organizationId,
      tag,
      status,
      startDate?.toISOString(),
      endDate?.toISOString(),
      page,
      rowsPerPage,
    ],
    () =>
      scormExportedFilesService.getExportedFiles(
        new ListRequestOfScormExportedFilesFilter({
          pageNumber: page,
          pageSize: rowsPerPage,
          parameters: new ScormExportedFilesFilter({
            tag: tag || undefined,
            status: status || undefined,
            // The bound is an instant and the picker gives a day, so the end of the chosen day is what
            // makes End Date inclusive.
            exportedFrom: getUtcDate(startDate),
            exportedTo: getUtcDate(endDate, { endOfDay: true }),
          }),
        })
      ),
    {
      enabled: !!organizationId,
      keepPreviousData: true,
      // ⛔ An attempt has settled when the SERVER REWROTE THE ROW, not when its status looks final. Status
      // cannot answer this: delivery writes Pending at export time only, so a retried row reads Failed for
      // the whole transfer and a failed retry leaves it reading Failed afterwards. Treating Failed as
      // settled dropped the entry on the very next refetch, which took the retry's own block and warning
      // with it and stopped the poll - so a retry that SUCCEEDED went on showing Failed with the stale
      // error until the operator changed a filter.
      onSuccess: (latest) =>
        setQueuedAtByRunFileId((previous) => {
          const settledIds = new Set(
            (latest.data ?? [])
              .filter(
                (file): file is ScormExportedFile & { runFileId: string } =>
                  !!file.runFileId &&
                  hasAnswered(previous[file.runFileId], file)
              )
              .map((file) => file.runFileId)
          );

          // Only what settled is dropped. An entry the screen stopped POLLING for is deliberately kept: the
          // warning tooltip reads this map, and forgetting an attempt that never reported would withdraw
          // that warning exactly when the evidence of a lost message is strongest. The map only grows by an
          // operator's own clicks, and it is cleared when the organization changes.
          const kept = Object.entries(previous).filter(
            ([runFileId]) => !settledIds.has(runFileId)
          );

          // Same object back when nothing dropped, so this cannot re-render on every poll.
          return kept.length === Object.keys(previous).length
            ? previous
            : Object.fromEntries(kept);
        }),
      // The upload is asynchronous, so nothing else would tell the grid that a status has moved. Polling is
      // what UPLOAD_WATCH_LIMIT_MS bounds - not the memory of the attempt: an attempt that has not reported
      // in two minutes will not be explained by another refetch, but the operator still needs telling.
      refetchInterval: (latest) =>
        (latest?.data ?? []).some(
          (file) =>
            !!file.runFileId &&
            isWithinUploadWatch(file.runFileId) &&
            !hasAnswered(queuedAtByRunFileId[file.runFileId], file)
        )
          ? POLL_INTERVAL_MS
          : false,
    }
  );

  const rows: ExportedScormFileRow[] = (data?.data ?? [])
    .filter(
      (file): file is ScormExportedFile & { runFileId: string } =>
        !!file.runFileId
    )
    .map((file) => ({ ...file, id: file.runFileId }));

  const rowById = (id: string) => rows.find((row) => row.id === id);

  // A row this screen queued and never saw settle. The upload is offered back, but the tooltip says the
  // previous attempt has not reported, so a second one is a decision rather than an assumption that the
  // first never happened.
  const isAwaitingAnUnreportedAttempt = (id: string) =>
    id in queuedAtByRunFileId && !isWithinUploadBlock(id);

  // Keyed by id and not by row so it answers for a selection made on an earlier page too, where the row
  // itself is no longer on screen to read a status from.
  const isUploadable = (id: string) => {
    const rowStatus = rowById(id)?.status;

    if (rowStatus === ScormExportFileDeliveryStatus.Uploaded) {
      return false;
    }

    // Failed and Pending alike. A settled attempt is retryable - the operator fixes the destination's
    // credentials and sends it again - but an attempt this screen queued a moment ago may still be in
    // flight, and the block is what stops a second one joining it. Failed needs it at least as much as
    // Pending: a retried row stays Failed for the whole transfer, because delivery writes Pending only at
    // export time, so its status never signals that something is under way.
    //
    // Past the block the row is offered back rather than held, because the screen cannot tell a slow attempt
    // from a lost one and holding it forever is the worse of the two mistakes.
    if (rowStatus !== undefined) {
      return !isWithinUploadBlock(id);
    }

    // No status to read, so the row is not on this page. Forwarded rather than guessed at: delivery re-reads
    // every row and skips what it already delivered, and a block resting on session state alone is one
    // nothing on this screen would ever lift.
    return true;
  };

  // Reads the message the server sent rather than the generated client's generic wording, which would
  // replace a usable reason with "a server side error occurred".
  const reportError = (error: any, messageId: string) =>
    setSnackbarMessage(
      new SnackbarMessageProps(
        error?.result?.title ??
          error?.result?.detail ??
          error?.data?.title ??
          t({ id: messageId }),
        Severity.Error
      )
    );

  const downloadFile = async (row: ExportedScormFileRow) => {
    try {
      saveFile(
        await scormExportedFilesService.downloadExportedFile(row.id),
        row.fileName ?? FALLBACK_FILE_NAME
      );
    } catch (error) {
      reportError(error, "exportedScormFilesDownloadError");
    }
  };

  // Failures are reported through the action's onError rather than caught here: the toolbar runs this one
  // without a confirmation dialog, so there is no Alert to render them in.
  const downloadFiles = async (ids: string[]) => {
    saveFile(
      await scormExportedFilesService.downloadExportedFiles(
        new ScormExportedFilesDownloadRequest({ runFileIds: ids })
      ),
      FALLBACK_ARCHIVE_NAME
    );
  };

  const uploadFiles = async (ids: string[]) => {
    // An id selected on an earlier page is not among the current rows, so its status cannot be read here.
    // Those are forwarded rather than guessed at: delivery re-reads every row and skips what it already
    // delivered, which is the only authoritative answer.
    const queueable = ids.filter((id) => isUploadable(id));
    const skippedCount = ids.length - queueable.length;

    if (queueable.length === 0) {
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "exportedScormFilesUploadAllSkipped" }, { skippedCount }),
          Severity.Warning
        )
      );

      return;
    }

    try {
      // The selection is published as several messages, so only what the service confirms it queued is
      // recorded here. Anything it could not queue stays selectable, and anything it did queue is skipped
      // on the next attempt - which is what keeps a retry from sending the same file to a customer twice.
      const { queuedRunFileIds } =
        await scormExportedFilesService.uploadExportedFiles(
          new ScormExportedFilesUploadRequest({ runFileIds: queueable })
        );

      const queued = queuedRunFileIds ?? [];
      const notQueuedCount = queueable.length - queued.length;

      const refusedNames = queueable
        .filter((id) => !queued.includes(id))
        .map((id) => rowById(id)?.fileName)
        .filter((fileName): fileName is string => !!fileName);

      const queuedAt = Date.now();

      // The baseline is read from the row ON SCREEN, which is the last value the server sent for it. The
      // attempt has answered when the server replaces that value - so a retry of a Failed row is
      // recognised even though its status is Failed before and after.
      setQueuedAtByRunFileId((previous) => ({
        ...previous,
        ...Object.fromEntries(
          queued.map((id) => {
            const row = rowById(id);

            return [
              id,
              { queuedAt, baselineUpdatedOn: row ? updatedOnOf(row) : undefined },
            ];
          })
        ),
      }));
      queryClient.invalidateQueries(LIST_QUERY_KEY);

      // Counts of what was queued, never of what was delivered: the push happens after this response and
      // each row's own status is where its outcome appears.
      const outcome = uploadOutcome(
        queued.length,
        notQueuedCount,
        skippedCount,
        refusedNames
      );

      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: outcome.messageId }, outcome.values),
          outcome.isWarning ? Severity.Warning : Severity.Success
        )
      );
    } catch (error) {
      reportError(error, "exportedScormFilesUploadError");
    }
  };

  // Thin adapters: ExpandableTable calls a column's View with only { value }, so the cells' other
  // dependencies are bound here. They are NOT memoised - see the note on the cells array below.
  const StatusCellView = ({ value }: { value: ExportedScormFileRow }) => (
    <StatusCell value={value} t={t} />
  );

  const ActionsCellView = ({ value }: { value: ExportedScormFileRow }) => (
    <ActionsCell
      value={value}
      t={t}
      isUploadable={isUploadable}
      isAwaitingAnUnreportedAttempt={isAwaitingAnUnreportedAttempt}
      onDownload={downloadFile}
      onUpload={setFileToUpload}
    />
  );

  // ⚠️ These adapters are rebuilt on every render, so React remounts each cell rather than updating it -
  // with the five second poll, that is every cell of every row. Moving the cells into their own files did
  // not change that: what fixes it is a STABLE component identity, which needs the handlers above wrapped
  // in useCallback. That belongs with the hook extraction, once the screen has been through QA once and a
  // regression can be attributed.
  const cells: Array<TableCellType> = [
    {
      accessor: "fileName",
      align: "left",
      type: "text",
      label: t({ id: "fileNameLabel" }),
      noSort: true,
    },
    {
      accessor: "tag",
      align: "left",
      type: "text",
      label: t({ id: "addExportCoursesTag" }),
      noSort: true,
    },
    {
      accessor: "organizationName",
      align: "left",
      type: "text",
      label: t({ id: "organization" }),
      noSort: true,
    },
    {
      accessor: "exportedOn",
      align: "left",
      type: "date",
      label: t({ id: "exportedScormFilesExportedColumn" }),
      noSort: true,
    },
    {
      accessor: "contentType",
      align: "left",
      type: "text",
      label: t({ id: "typeLabel" }),
      noSort: true,
    },
    {
      accessor: "status",
      align: "left",
      type: "node",
      label: t({ id: "statusLabel" }),
      noSort: true,
      View: StatusCellView,
    },
    {
      accessor: "actions",
      align: "right",
      type: "node",
      label: t({ id: "actions" }),
      noSort: true,
      View: ActionsCellView,
    },
  ];

  const bulkActions: ExecuteMultipleAction[] = [
    {
      onExecuteMultiple: downloadFiles,
      onError: (error) => reportError(error, "exportedScormFilesDownloadError"),
      buttonLabel: t({ id: "exportedScormFilesDownloadSelected" }),
    },
    {
      onExecuteMultiple: uploadFiles,
      dialogLabel: "exportedScormFilesUploadConfirmation",
      buttonLabel: t({ id: "exportedScormFilesUploadSelected" }),
      // Same rule the row icon already applies, so a delivered file cannot be sent from the toolbar either.
      // An id from another page has no status to read and stays eligible: delivery re-reads every row and
      // skips what it already delivered, which is the only authoritative answer.
      isEnabledFor: (ids) => ids.some((id) => isUploadable(id)),
    },
  ];

  if (!organizationId) {
    return <Loader />;
  }

  return (
    <Container>
      <ExportedScormFilesFilters
        t={t}
        tagOptions={tagsQuery.data?.data ?? []}
        tag={tag}
        status={status}
        startDate={startDate}
        endDate={endDate}
        startDateError={startDateError}
        endDateError={endDateError}
        onStartDateChange={applyStartDate}
        onEndDateChange={applyEndDate}
        onStartDateError={setStartDateError}
        onEndDateError={setEndDateError}
      />

      {/* Remounting on the breadcrumb organization drops the table's own selection, which the branch above
          cannot reach: it clears the queued-attempt map and the pending single upload for exactly this
          reason, and a surviving selection describes rows the new scope may not even be able to see. */}
      <ExpandableTable
        key={organizationId}
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows,
          // Only while there is nothing to show. keepPreviousData means isFetching is true on
          // every background poll, and the table mounts a progress bar on loading - so the grid
          // jumped every five seconds for as long as a row was queued.
          loading: isFetching && rows.length === 0,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          executeMultiple: bulkActions,
          numSelectedMessageId: "exportedScormFilesNumSelected",
          titleComponent: (
            <Typography variant="h6">
              <T id="exportedScormFiles" />
            </Typography>
          ),
          collapsable: true,
          hideEmptyRows: true,
        }}
      />

      {/* The row icon pushes a file to a live customer destination, exactly as the bulk button does, so it
          asks the same question. It sits next to the download icon in the same cell, and a delivered file
          cannot be sent again from this screen, so a misclick is both easy and unrecoverable. */}
      <ConfirmDialog
        dialogTitleKey="confirmButton"
        rawContent={
          <T
            id="exportedScormFilesUploadOneConfirmation"
            values={{ fileName: fileToUpload?.fileName ?? FALLBACK_FILE_NAME }}
          />
        }
        open={!!fileToUpload}
        onCancel={() => setFileToUpload(null)}
        // Awaited before the dialog closes, so ConfirmDialog's own in-flight guard applies: it disables both
        // buttons, shows a spinner and renders a rejection in its Alert. Closing first forfeits all three and
        // leaves the row clickable while the publish is still travelling.
        handleConfirm={async () => {
          if (fileToUpload?.id) {
            await uploadFiles([fileToUpload.id]);
          }

          setFileToUpload(null);
        }}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default ExportedScormFiles;
